/**
 * BATCH 22 PART 20 — Marketing Workflow Templates 239–250 (Braze)
 * All 12 workflows use Braze cross-channel capabilities: push, in-app, email, SMS, content cards.
 *
 * DESIGN RULES:
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers have EMPTY inputFields — an AI extraction node follows
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Node names must be descriptive (e.g., "Braze: Send push notification", not "API Call")
 * 7. Each workflow has 8 nodes with proper edges
 */

// ============ HELPER FUNCTIONS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function brazePostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://rest.iad-01.braze.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {braze_api_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function brazeGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://rest.iad-01.braze.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {braze_api_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 22 PART 20 — TEMPLATES 239–250 (Braze Marketing Workflows)
// =============================================================================

export const ELITE_BATCH22_PART20: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 239. LOYALTY MILESTONE CELEBRATION — Pattern H
  // ===================================================================

  "Loyalty Milestone Celebration": {
    nodes: [
      trigger(
        "Webhook: Braze loyalty milestone event received",
        "webhook_app",
        {
          source: "Braze",
          event: "loyalty.milestone.reached",
          howItStarts:
            "Braze fires a webhook when a customer reaches a loyalty milestone such as points threshold, tier upgrade, or purchase count milestone",
          setup:
            "Configure Braze to POST loyalty milestone events to this workflow URL. Include external_id and milestone details in the payload.",
          inputFields: [],
        },
      ),

      // 2. AI extract milestone details
      aiNode(
        "2",
        "AI: Extract loyalty milestone details from webhook payload",
        280,
        "You are a loyalty program data analyst. Extract the customer external_id, email, first_name, milestone_type (tier_upgrade, points_threshold, purchase_count), new_tier, current_points, total_lifetime_purchases, and milestone_label from the webhook payload regardless of format. Return JSON.",
        "Extract loyalty milestone details from: {webhook_payload}",
        [
          { field: "external_id", as: "external_id", type: "string" },
          { field: "email", as: "customer_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "milestone_type", as: "milestone_type", type: "string" },
          { field: "new_tier", as: "new_tier", type: "string" },
          { field: "current_points", as: "current_points", type: "number" },
          { field: "milestone_label", as: "milestone_label", type: "string" },
        ],
      ),

      // 3. Braze: Lookup user profile + loyalty data + LTV
      brazePostNode(
        "3",
        "Braze: Fetch user profile with loyalty attributes and LTV",
        520,
        "users/export/ids",
        [
          { key: "external_ids", value: '["{ external_id }"]' },
          {
            key: "fields_to_export",
            value:
              '["email","custom_attributes","total_revenue","push_subscribe","email_subscribe"]',
          },
        ],
        [
          {
            name: "users[0].total_revenue",
            customName: "lifetime_revenue",
            type: "number",
          },
          {
            name: "users[0].custom_attributes.loyalty_tier",
            customName: "loyalty_tier",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.rewards_redeemed",
            customName: "rewards_redeemed",
            type: "number",
          },
          {
            name: "users[0].push_subscribe",
            customName: "push_subscribed",
            type: "string",
          },
        ],
      ),

      // 4. Condition: VIP tier (Gold or Platinum)?
      n("4", "condition", "Is customer VIP tier (Gold or Platinum)?", 760, {
        conditionData: [
          {
            variable: "loyalty_tier",
            value: "Gold",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "loyalty_tier",
            value: "Platinum",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 5. Braze: Send celebration push + email with exclusive reward
      brazePostNode(
        "5",
        "Braze: Send celebration push + email with VIP-exclusive reward",
        1000,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"apple_push":{"alert":"Congratulations {first_name}! You reached {milestone_label}. Enjoy your exclusive VIP reward inside.","extra":{"deep_link":"app://loyalty/milestone"}},"email":{"app_id":"{braze_app_id}","subject":"You did it, {first_name}! Celebrate your {milestone_label} milestone","body":"<h1>Congratulations on {milestone_label}!</h1><p>As a valued {loyalty_tier} member with {current_points} points, here is your exclusive milestone reward: 25% off your next purchase. Use code MILESTONE25.</p><a href=https://shop.example.com/rewards>Redeem Now</a>"}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "celebration_dispatch_id",
            type: "string",
          },
          {
            name: "message_variations[0].send_id",
            customName: "celebration_send_id",
            type: "string",
          },
        ],
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait 5 days for reward redemption window", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Condition: Reward redeemed?
      n("7", "condition", "Has customer redeemed the milestone reward?", 1480, {
        conditionData: [
          {
            variable: "rewards_redeemed",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Braze: Send redemption reminder or next-tier teaser via content card
      brazePostNode(
        "8",
        "Braze: Send redemption reminder content card + next-tier teaser",
        1720,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"content_card":{"title":"Your {milestone_label} reward is waiting!","description":"Don\'t forget your exclusive milestone reward expires in 2 days. You\'re {current_points} points closer to the next tier!","extras":{"deep_link":"app://loyalty/rewards","reward_code":"MILESTONE25"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "reminder_dispatch_id",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // VIP tier -> exclusive reward
      e("4", "5", "output_2"), // Non-VIP -> standard reward (same node, different offer copy could vary)
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"), // Not redeemed -> send reminder
      e("7", "8", "output_1"), // Redeemed -> send next-tier teaser
    ],
    summary:
      "Celebrates loyalty milestones via Braze cross-channel messaging. A webhook fires when a customer hits a tier upgrade, points threshold, or purchase count milestone. AI extracts milestone details, Braze fetches the user profile and LTV, a VIP-tier condition gates exclusive vs. standard reward messaging, a celebration push notification and email with reward code are sent, and after a 5-day redemption window, a content card reminder or next-tier teaser is delivered based on redemption status.",
  },

  // ===================================================================
  // 240. REFERRAL INVITATION JOURNEY — Pattern J
  // ===================================================================

  "Referral Invitation Journey": {
    nodes: [
      trigger(
        "Webhook: Braze positive customer signal received",
        "webhook_app",
        {
          source: "Braze",
          event: "customer.positive_signal",
          howItStarts:
            "Braze fires a webhook when a customer triggers a positive signal such as high NPS score, 5-star review, repeat purchase milestone, or high engagement score",
          setup:
            "Configure Braze custom events or Currents to POST positive signals to this workflow URL when NPS >= 8, review >= 4 stars, or 5+ purchases.",
          inputFields: [],
        },
      ),

      // 2. Braze: Lookup customer health + prior advocacy history
      aiNode(
        "2",
        "AI: Extract positive signal details and customer identifier",
        280,
        "You are a customer advocacy analyst. Extract external_id, email, first_name, signal_type (nps, review, repeat_purchase, engagement), signal_value (score/rating/count), product_name, and days_since_last_advocacy_ask from the webhook payload regardless of format. Return JSON.",
        "Extract positive signal details from: {webhook_payload}",
        [
          { field: "external_id", as: "external_id", type: "string" },
          { field: "email", as: "customer_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "signal_type", as: "signal_type", type: "string" },
          { field: "signal_value", as: "signal_value", type: "number" },
          {
            field: "days_since_last_advocacy_ask",
            as: "days_since_ask",
            type: "number",
          },
        ],
      ),

      // 3. Condition: Eligible and not recently asked (>30 days)?
      n(
        "3",
        "condition",
        "Is customer eligible for referral ask (not asked in 30+ days)?",
        520,
        {
          conditionData: [
            {
              variable: "days_since_ask",
              value: "30",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
            {
              variable: "signal_value",
              value: "7",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 4. Braze: Send referral ask email with personalized context + incentive
      brazePostNode(
        "4",
        "Braze: Send personalized referral ask email with incentive",
        760,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"email":{"app_id":"{braze_app_id}","subject":"{first_name}, share the love — give $20, get $20","body":"<h1>Loving your experience?</h1><p>We noticed you gave us a {signal_value} {signal_type} — thank you! Share your unique referral link with friends and you both get $20 off.</p><a href=https://refer.example.com/{external_id}>Share Your Link</a>"},"apple_push":{"alert":"{first_name}, you loved it! Share with friends and both get $20.","extra":{"deep_link":"app://referral/share"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "referral_dispatch_id",
            type: "string",
          },
          {
            name: "message_variations[0].send_id",
            customName: "referral_send_id",
            type: "string",
          },
        ],
      ),

      // 5. Delay 5 days
      n("5", "delayAction", "Wait 5 days for referral action", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 6. Braze: Check if referral was completed
      brazePostNode(
        "6",
        "Braze: Check referral completion status via user attributes",
        1240,
        "users/export/ids",
        [
          { key: "external_ids", value: '["{ external_id }"]' },
          { key: "fields_to_export", value: '["custom_attributes"]' },
        ],
        [
          {
            name: "users[0].custom_attributes.referral_sent_count",
            customName: "referral_sent_count",
            type: "number",
          },
          {
            name: "users[0].custom_attributes.last_referral_date",
            customName: "last_referral_date",
            type: "string",
          },
        ],
      ),

      // 7. Condition: Referral accepted/completed?
      n("7", "condition", "Has customer sent at least one referral?", 1480, {
        conditionData: [
          {
            variable: "referral_sent_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Google Sheets: Log advocacy status + send reminder or thank-you
      sheetsNode(
        "8",
        "Google Sheets: Log referral advocacy status and outcome",
        1720,
        "Referral_Advocacy",
        '[["{external_id}","{customer_email}","{first_name}","{signal_type}","{signal_value}","{referral_sent_count}","{last_referral_date}","{new Date().toISOString()}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // Eligible -> send referral ask
      e("3", "8", "output_2"), // Not eligible -> log and skip
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"), // Both paths -> log outcome
    ],
    summary:
      "Drives referral advocacy through Braze cross-channel outreach after positive customer signals. When a high NPS score, positive review, or repeat purchase milestone is detected, AI extracts the signal details, an eligibility check ensures the customer has not been asked in 30+ days, a personalized referral email and push notification with $20 dual-sided incentive are sent, and after a 5-day window the referral completion status is checked and logged to Google Sheets for advocacy tracking.",
  },

  // ===================================================================
  // 241. WEBINAR/EVENT REMINDER JOURNEY — Pattern I
  // ===================================================================

  "Webinar/Event Reminder Journey": {
    nodes: [
      trigger(
        "Webhook: Braze webinar/event registration received",
        "webhook_app",
        {
          source: "Braze",
          event: "event.registration.completed",
          howItStarts:
            "Braze fires a webhook when a user registers for a webinar or live event, triggering a multi-touch reminder sequence leading up to the event date",
          setup:
            "Configure Braze to POST event registration data to this workflow URL. Include external_id, event name, date/time, and registration details.",
          inputFields: [],
        },
      ),

      // 2. AI extract registration + event details
      aiNode(
        "2",
        "AI: Extract event registration details and schedule",
        280,
        "You are an event operations analyst. Extract external_id, email, first_name, event_name, event_date, event_time, event_timezone, event_url, speaker_name, and topic_category from the webhook payload regardless of format. Return JSON.",
        "Extract webinar/event registration details from: {webhook_payload}",
        [
          { field: "external_id", as: "external_id", type: "string" },
          { field: "email", as: "registrant_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "event_name", as: "event_name", type: "string" },
          { field: "event_date", as: "event_date", type: "string" },
          { field: "event_url", as: "event_url", type: "string" },
          { field: "speaker_name", as: "speaker_name", type: "string" },
        ],
      ),

      // 3. Braze: Lookup lifecycle stage + content history
      brazePostNode(
        "3",
        "Braze: Fetch user lifecycle stage and past event attendance",
        520,
        "users/export/ids",
        [
          { key: "external_ids", value: '["{ external_id }"]' },
          {
            key: "fields_to_export",
            value: '["custom_attributes","email_subscribe","push_subscribe"]',
          },
        ],
        [
          {
            name: "users[0].custom_attributes.lifecycle_stage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.events_attended",
            customName: "events_attended",
            type: "number",
          },
          {
            name: "users[0].push_subscribe",
            customName: "push_opted_in",
            type: "string",
          },
        ],
      ),

      // 4. Condition: First-time attendee vs returning?
      n(
        "4",
        "condition",
        "Is this the registrant's first event (events_attended = 0)?",
        760,
        {
          conditionData: [
            {
              variable: "events_attended",
              value: "0",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. Braze: Send contextual confirmation email + push with event details
      brazePostNode(
        "5",
        "Braze: Send event confirmation email + calendar push reminder",
        1000,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"email":{"app_id":"{braze_app_id}","subject":"You\'re registered for {event_name}!","body":"<h1>See you at {event_name}!</h1><p>Hi {first_name}, you\'re confirmed for {event_name} on {event_date} with {speaker_name}. Add it to your calendar and join via your unique link.</p><a href={event_url}>Join Event</a><p>Tip: Prepare questions in advance for the Q&A session.</p>"},"apple_push":{"alert":"Confirmed! {event_name} on {event_date}. We\'ll remind you before it starts.","extra":{"deep_link":"app://events/{event_name}"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "confirm_dispatch_id",
            type: "string",
          },
        ],
      ),

      // 6. Delay 3 days (or until day before event)
      n(
        "6",
        "delayAction",
        "Wait 3 days for day-before reminder timing",
        1240,
        {
          delayDetails: { duration: 3, unit: "days" },
        },
      ),

      // 7. Condition: Has registrant engaged with confirmation (opened/clicked)?
      n(
        "7",
        "condition",
        "Did registrant open or click confirmation email?",
        1480,
        {
          conditionData: [
            {
              variable: "push_opted_in",
              value: "opted_in",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Braze: Send day-of push reminder + in-app message with join link
      brazePostNode(
        "8",
        "Braze: Send day-of push reminder + in-app join CTA",
        1720,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"apple_push":{"alert":"{event_name} starts soon! Join now with {speaker_name}.","extra":{"deep_link":"{event_url}"}},"in_app_message":{"type":"modal","header":"{event_name} is Live!","body":"Join {speaker_name} now. Tap to enter the session.","buttons":[{"text":"Join Now","uri":"{event_url}"}]}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "reminder_dispatch_id",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // First-time -> extra onboarding context
      e("4", "5", "output_2"), // Returning -> standard confirmation
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // Push opted-in -> push + in-app
      e("7", "8", "output_2"), // Not opted-in -> in-app only fallback
    ],
    summary:
      "Orchestrates a webinar/event reminder journey via Braze cross-channel messaging. When a user registers, AI extracts event details (name, date, speaker, URL), Braze fetches the user's lifecycle stage and attendance history, a first-time vs. returning attendee condition personalizes the flow, a confirmation email and push notification with calendar details are sent, and after a 3-day delay a day-of push reminder with in-app join CTA drives live attendance.",
  },

  // ===================================================================
  // 242. WEBINAR ATTENDEE FOLLOW-UP — Pattern I
  // ===================================================================

  "Webinar Attendee Follow-Up": {
    nodes: [
      trigger(
        "Webhook: Braze webinar attendance event received",
        "webhook_app",
        {
          source: "Braze",
          event: "webinar.attendance.completed",
          howItStarts:
            "Braze fires a webhook when a webinar concludes and attendance data is available, triggering personalized follow-up based on whether the user attended or was a no-show",
          setup:
            "Configure Braze to POST webinar completion events with attendance status, duration watched, and engagement score to this workflow URL.",
          inputFields: [],
        },
      ),

      // 2. AI extract attendance details
      aiNode(
        "2",
        "AI: Extract webinar attendance and engagement data",
        280,
        "You are a webinar analytics specialist. Extract external_id, email, first_name, webinar_name, attended (yes/no), duration_watched_minutes, total_duration_minutes, engagement_score, questions_asked, and recording_url from the webhook payload regardless of format. Return JSON.",
        "Extract webinar attendance data from: {webhook_payload}",
        [
          { field: "external_id", as: "external_id", type: "string" },
          { field: "email", as: "attendee_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "webinar_name", as: "webinar_name", type: "string" },
          { field: "attended", as: "attended", type: "string" },
          { field: "engagement_score", as: "engagement_score", type: "number" },
          { field: "recording_url", as: "recording_url", type: "string" },
        ],
      ),

      // 3. Braze: Lookup lifecycle stage + content history
      brazePostNode(
        "3",
        "Braze: Fetch user lifecycle stage and content consumption history",
        520,
        "users/export/ids",
        [
          { key: "external_ids", value: '["{ external_id }"]' },
          {
            key: "fields_to_export",
            value: '["custom_attributes","email_subscribe"]',
          },
        ],
        [
          {
            name: "users[0].custom_attributes.lifecycle_stage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.content_consumed_count",
            customName: "content_consumed_count",
            type: "number",
          },
          {
            name: "users[0].email_subscribe",
            customName: "email_subscribed",
            type: "string",
          },
        ],
      ),

      // 4. Condition: Attended vs no-show?
      n(
        "4",
        "condition",
        "Did user attend the webinar (attended = yes)?",
        760,
        {
          conditionData: [
            {
              variable: "attended",
              value: "yes",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. Braze: Send contextual follow-up email (recap + recording for attendee, recording for no-show)
      brazePostNode(
        "5",
        "Braze: Send post-webinar follow-up email with recording and recap",
        1000,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"email":{"app_id":"{braze_app_id}","subject":"Your {webinar_name} recap + recording inside","body":"<h1>Thanks for joining {webinar_name}!</h1><p>Hi {first_name}, here\'s your recording and key takeaways from the session. Your engagement score was {engagement_score}/100.</p><a href={recording_url}>Watch Recording</a><p>Ready for the next step? Check out our recommended resources below.</p>"},"content_card":{"title":"{webinar_name} Recording Available","description":"Watch the full recording and download the slides.","extras":{"deep_link":"{recording_url}"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "followup_dispatch_id",
            type: "string",
          },
        ],
      ),

      // 6. Delay 4 days
      n("6", "delayAction", "Wait 4 days for content consumption", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),

      // 7. Condition: Consumed follow-up content (content count increased)?
      n(
        "7",
        "condition",
        "Has user consumed follow-up content (content count > previous)?",
        1480,
        {
          conditionData: [
            {
              variable: "content_consumed_count",
              value: "2",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Braze: Send progressive content (case study for engaged, alternative resource for passive)
      brazePostNode(
        "8",
        "Braze: Send progressive content — case study or meeting invite",
        1720,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"email":{"app_id":"{braze_app_id}","subject":"{first_name}, see how others applied {webinar_name} insights","body":"<h1>From Insights to Results</h1><p>Based on your interest in {webinar_name}, here\'s a case study showing real outcomes. Ready to discuss how this applies to your situation?</p><a href=https://example.com/case-studies>Read Case Study</a><br/><a href=https://example.com/book-meeting>Book a Meeting</a>"},"apple_push":{"alert":"New case study related to {webinar_name} — see real results inside.","extra":{"deep_link":"app://content/case-studies"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "progressive_dispatch_id",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Attended -> recap + recording follow-up
      e("4", "5", "output_2"), // No-show -> recording + re-engage
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // Engaged -> case study + meeting invite
      e("7", "8", "output_2"), // Passive -> alternative resource push
    ],
    summary:
      "Follows up with webinar attendees via Braze cross-channel messaging after a webinar concludes. AI extracts attendance data including engagement score and watch duration, Braze fetches lifecycle stage and content history, an attended vs. no-show condition personalizes the follow-up, a recap email with recording link and content card are delivered, and after a 4-day delay a progressive content email with case study or meeting invite is sent based on content consumption behavior.",
  },

  // ===================================================================
  // 243. TRAVEL BOOKING ABANDONMENT — Pattern B (travel/booking specific)
  // ===================================================================

  "Travel Booking Abandonment": {
    nodes: [
      trigger(
        "Webhook: Braze travel booking abandonment event",
        "webhook_app",
        {
          source: "Braze",
          event: "booking.abandoned",
          howItStarts:
            "Braze fires a webhook when a user abandons a travel booking mid-flow, such as a hotel reservation, flight search, or vacation package that was not completed",
          setup:
            "Configure Braze custom event tracking to POST booking abandonment events to this workflow URL when a user exits the booking funnel without completing payment.",
          inputFields: [],
        },
      ),

      // 2. AI extract booking/trip details
      aiNode(
        "2",
        "AI: Extract abandoned travel booking details",
        280,
        "You are a travel booking recovery analyst. Extract external_id, email, first_name, booking_type (hotel, flight, vacation_package, car_rental), destination_city, check_in_date, check_out_date, trip_total_price, currency, number_of_travelers, and property_or_flight_name from the webhook payload regardless of format. Return JSON.",
        "Extract travel booking abandonment details from: {webhook_payload}",
        [
          { field: "external_id", as: "external_id", type: "string" },
          { field: "email", as: "traveler_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "booking_type", as: "booking_type", type: "string" },
          { field: "destination_city", as: "destination_city", type: "string" },
          { field: "trip_total_price", as: "trip_total_price", type: "number" },
          {
            field: "property_or_flight_name",
            as: "property_name",
            type: "string",
          },
        ],
      ),

      // 3. Braze: Lookup traveler profile + booking history
      brazePostNode(
        "3",
        "Braze: Fetch traveler profile and past booking history",
        520,
        "users/export/ids",
        [
          { key: "external_ids", value: '["{ external_id }"]' },
          {
            key: "fields_to_export",
            value: '["custom_attributes","email_subscribe","push_subscribe"]',
          },
        ],
        [
          {
            name: "users[0].custom_attributes.total_bookings",
            customName: "total_bookings",
            type: "number",
          },
          {
            name: "users[0].custom_attributes.loyalty_status",
            customName: "traveler_loyalty",
            type: "string",
          },
          {
            name: "users[0].push_subscribe",
            customName: "push_subscribed",
            type: "string",
          },
        ],
      ),

      // 4. Condition: High-value trip (> $500)?
      n("4", "condition", "Is abandoned booking value over $500?", 760, {
        conditionData: [
          {
            variable: "trip_total_price",
            value: "500",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Braze: Send first rescue email + push with trip details and urgency
      brazePostNode(
        "5",
        "Braze: Send trip rescue email + push with availability urgency",
        1000,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"email":{"app_id":"{braze_app_id}","subject":"{first_name}, your trip to {destination_city} is waiting!","body":"<h1>Still dreaming of {destination_city}?</h1><p>Your {booking_type} at {property_name} for ${trip_total_price} is still available — but availability is limited. Complete your booking before prices change.</p><a href=https://travel.example.com/resume-booking/{external_id}>Complete Booking</a>"},"apple_push":{"alert":"Your {destination_city} trip at {property_name} is still available! Prices may change soon.","extra":{"deep_link":"app://booking/resume"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "rescue1_dispatch_id",
            type: "string",
          },
        ],
      ),

      // 6. Delay 12 hours
      n(
        "6",
        "delayAction",
        "Wait 12 hours before second rescue attempt",
        1240,
        {
          delayDetails: { duration: 12, unit: "hours" },
        },
      ),

      // 7. Condition: Still not booked?
      n(
        "7",
        "condition",
        "Has traveler still not completed the booking?",
        1480,
        {
          conditionData: [
            {
              variable: "total_bookings",
              value: "0",
              comparison_operator: "EQUAL TO",
              logical_operator: "OR",
            },
            {
              variable: "traveler_loyalty",
              value: "none",
              comparison_operator: "EQUAL TO",
              logical_operator: "OR",
            },
          ],
        },
      ),

      // 8. Braze: Send second rescue with price alert + social proof via SMS + in-app
      brazePostNode(
        "8",
        "Braze: Send price alert SMS + in-app with social proof rescue",
        1720,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"sms":{"app_id":"{braze_app_id}","body":"{first_name}, prices for {property_name} in {destination_city} may increase. 47 other travelers are viewing this right now. Book now: https://travel.example.com/resume/{external_id}"},"in_app_message":{"type":"modal","header":"Your {destination_city} Trip","body":"47 travelers are looking at {property_name}. Lock in your rate of ${trip_total_price} before it\'s gone.","buttons":[{"text":"Complete Booking","uri":"https://travel.example.com/resume-booking/{external_id}"}]}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "rescue2_dispatch_id",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // High-value -> premium rescue with urgency
      e("4", "5", "output_2"), // Lower-value -> standard rescue
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // Still not booked -> second rescue SMS + in-app
      e("7", "8", "output_2"), // Already booked -> skip (edge exists but booking complete)
    ],
    summary:
      "Rescues abandoned travel bookings through Braze multi-channel outreach. When a user abandons a hotel, flight, or vacation package booking, AI extracts trip details including destination, property, and price. Braze fetches the traveler profile and booking history, a high-value trip condition tailors urgency messaging, a first rescue email and push notification highlight availability pressure, and after a 12-hour delay a second rescue via SMS and in-app message with social proof drives booking completion.",
  },

  // ===================================================================
  // 244. TRANSACTIONAL TO MARKETING BRIDGE — Pattern E
  // ===================================================================

  "Transactional to Marketing Bridge": {
    nodes: [
      trigger(
        "Webhook: Braze order confirmation event received",
        "webhook_app",
        {
          source: "Braze",
          event: "order.confirmed",
          howItStarts:
            "Braze fires a webhook when an order is confirmed, initiating a journey that transitions the customer from transactional communications to marketing engagement",
          setup:
            "Configure Braze to POST order confirmation events to this workflow URL. Include external_id, order details, and marketing opt-in status.",
          inputFields: [],
        },
      ),

      // 2. AI extract order details
      aiNode(
        "2",
        "AI: Extract order confirmation and customer details",
        280,
        "You are an e-commerce order analyst. Extract external_id, email, first_name, order_id, order_total, product_names (array), category, is_first_purchase (yes/no), and marketing_opted_in (yes/no) from the webhook payload regardless of format. Return JSON.",
        "Extract order confirmation details from: {webhook_payload}",
        [
          { field: "external_id", as: "external_id", type: "string" },
          { field: "email", as: "customer_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "order_total", as: "order_total", type: "number" },
          { field: "product_names", as: "product_names", type: "string" },
          {
            field: "is_first_purchase",
            as: "is_first_purchase",
            type: "string",
          },
        ],
      ),

      // 3. Braze: Lookup customer tenure + VIP + marketing consent
      brazePostNode(
        "3",
        "Braze: Fetch customer profile, VIP status, and marketing consent",
        520,
        "users/export/ids",
        [
          { key: "external_ids", value: '["{ external_id }"]' },
          {
            key: "fields_to_export",
            value:
              '["custom_attributes","email_subscribe","push_subscribe","total_revenue"]',
          },
        ],
        [
          {
            name: "users[0].total_revenue",
            customName: "lifetime_revenue",
            type: "number",
          },
          {
            name: "users[0].custom_attributes.vip_status",
            customName: "vip_status",
            type: "string",
          },
          {
            name: "users[0].email_subscribe",
            customName: "email_subscribe_status",
            type: "string",
          },
          {
            name: "users[0].push_subscribe",
            customName: "push_subscribe_status",
            type: "string",
          },
        ],
      ),

      // 4. Condition: First purchase?
      n("4", "condition", "Is this the customer's first purchase?", 760, {
        conditionData: [
          {
            variable: "is_first_purchase",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Braze: Send thank you + getting started email with product education
      brazePostNode(
        "5",
        "Braze: Send order thank-you email with product care tips",
        1000,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"email":{"app_id":"{braze_app_id}","subject":"Thanks for your order, {first_name}! Here\'s what\'s next","body":"<h1>Order #{order_id} Confirmed!</h1><p>Thank you for purchasing {product_names}. Total: ${order_total}.</p><p>Here are tips to get the most from your purchase, plus a sneak peek at what we\'re working on next.</p><a href=https://shop.example.com/getting-started>Getting Started Guide</a>"},"content_card":{"title":"Welcome, {first_name}!","description":"Get the most from {product_names} with our getting started guide.","extras":{"deep_link":"app://orders/{order_id}"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "thankyou_dispatch_id",
            type: "string",
          },
        ],
      ),

      // 6. Delay 7 days
      n(
        "6",
        "delayAction",
        "Wait 7 days for product delivery and experience",
        1240,
        {
          delayDetails: { duration: 7, unit: "days" },
        },
      ),

      // 7. Condition: Marketing opted-in?
      n("7", "condition", "Is customer opted in to marketing emails?", 1480, {
        conditionData: [
          {
            variable: "email_subscribe_status",
            value: "opted_in",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Braze: Send marketing bridge email with preference center + cross-sell
      brazePostNode(
        "8",
        "Braze: Send marketing opt-in bridge email with cross-sell",
        1720,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"email":{"app_id":"{braze_app_id}","subject":"{first_name}, get early access to new arrivals + exclusive offers","body":"<h1>Stay in the Loop</h1><p>Enjoying {product_names}? Join our insider list for early access to new products, exclusive offers, and personalized recommendations based on your interests.</p><a href=https://shop.example.com/preferences/{external_id}>Set Your Preferences</a><h2>You Might Also Like</h2><p>Based on your purchase, check out our curated picks.</p><a href=https://shop.example.com/recommendations/{external_id}>See Recommendations</a>"},"apple_push":{"alert":"Unlock exclusive deals and early access! Set your preferences now.","extra":{"deep_link":"app://preferences"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "bridge_dispatch_id",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // First purchase -> extra onboarding + tips
      e("4", "5", "output_2"), // Returning -> standard thank you
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // Opted-in -> cross-sell focused
      e("7", "8", "output_2"), // Not opted-in -> opt-in ask + preference center
    ],
    summary:
      "Bridges transactional order confirmations into marketing engagement via Braze. When an order is confirmed, AI extracts order details and first-purchase status, Braze fetches VIP status and marketing consent, a first-purchase condition tailors the onboarding experience, a thank-you email with product care tips and content card are sent, and after a 7-day delivery window a marketing opt-in bridge email with preference center and cross-sell recommendations drives ongoing engagement.",
  },

  // ===================================================================
  // 245. GEO-TRIGGERED STORE VISIT JOURNEY — Pattern A
  // ===================================================================

  "Geo-Triggered Store Visit Journey": {
    nodes: [
      trigger("Webhook: Braze geofence entry event detected", "webhook_app", {
        source: "Braze",
        event: "geofence.entry",
        howItStarts:
          "Braze fires a webhook when a user enters a geofence around a retail store location, triggering a real-time store visit push notification and follow-up engagement",
        setup:
          "Configure Braze geofence locations for each retail store. Enable location tracking in the Braze SDK and POST geofence entry events to this workflow URL.",
        inputFields: [],
      }),

      // 2. AI extract geofence + user details
      aiNode(
        "2",
        "AI: Extract geofence entry and store location details",
        280,
        "You are a location-based marketing analyst. Extract external_id, email, first_name, store_name, store_id, store_address, latitude, longitude, geofence_radius_meters, and entry_timestamp from the webhook payload regardless of format. Return JSON.",
        "Extract geofence store entry details from: {webhook_payload}",
        [
          { field: "external_id", as: "external_id", type: "string" },
          { field: "email", as: "customer_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "store_name", as: "store_name", type: "string" },
          { field: "store_id", as: "store_id", type: "string" },
          { field: "store_address", as: "store_address", type: "string" },
        ],
      ),

      // 3. Braze: Lookup user profile + purchase history at this store
      brazePostNode(
        "3",
        "Braze: Fetch user profile with in-store purchase history",
        520,
        "users/export/ids",
        [
          { key: "external_ids", value: '["{ external_id }"]' },
          {
            key: "fields_to_export",
            value: '["custom_attributes","push_subscribe","total_revenue"]',
          },
        ],
        [
          {
            name: "users[0].custom_attributes.in_store_visits",
            customName: "in_store_visits",
            type: "number",
          },
          {
            name: "users[0].custom_attributes.last_store_purchase_date",
            customName: "last_store_purchase",
            type: "string",
          },
          {
            name: "users[0].push_subscribe",
            customName: "push_subscribed",
            type: "string",
          },
          {
            name: "users[0].total_revenue",
            customName: "lifetime_revenue",
            type: "number",
          },
        ],
      ),

      // 4. Condition: Existing customer (has previous visits)?
      n(
        "4",
        "condition",
        "Has customer visited this store before (visits > 0)?",
        760,
        {
          conditionData: [
            {
              variable: "in_store_visits",
              value: "0",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. Braze: Send real-time push notification with store-specific offer
      brazePostNode(
        "5",
        "Braze: Send geo-triggered push with store welcome + offer",
        1000,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"apple_push":{"alert":"Welcome to {store_name}, {first_name}! Show this notification for 15% off your purchase today.","extra":{"deep_link":"app://store/{store_id}/offer","offer_code":"GEO15","store_id":"{store_id}"}},"in_app_message":{"type":"slideup","header":"Welcome to {store_name}!","body":"Show this to a team member for 15% off. Valid today only.","buttons":[{"text":"View Offer","uri":"app://store/{store_id}/offer"}]}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "geo_push_dispatch_id",
            type: "string",
          },
        ],
      ),

      // 6. Delay 24 hours
      n("6", "delayAction", "Wait 24 hours for post-visit follow-up", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),

      // 7. Condition: Made a purchase during visit?
      n(
        "7",
        "condition",
        "Did customer make a purchase during the store visit?",
        1480,
        {
          conditionData: [
            {
              variable: "last_store_purchase",
              value: "",
              comparison_operator: "NOT EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Braze: Send post-visit follow-up email (thank you or return incentive)
      brazePostNode(
        "8",
        "Braze: Send post-visit follow-up email with thank you or return offer",
        1720,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"email":{"app_id":"{braze_app_id}","subject":"Thanks for visiting {store_name}, {first_name}!","body":"<h1>Great seeing you at {store_name}!</h1><p>We hope you enjoyed your visit to {store_address}. Rate your experience and earn bonus loyalty points.</p><a href=https://shop.example.com/store-feedback/{store_id}>Rate Your Visit</a><p>Can\'t wait to see you again — here\'s 10% off your next visit.</p>"},"content_card":{"title":"10% off your next {store_name} visit","description":"Use code RETURN10 on your next in-store purchase.","extras":{"offer_code":"RETURN10","deep_link":"app://store/{store_id}"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "followup_dispatch_id",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Returning customer -> personalized welcome
      e("4", "5", "output_2"), // First-time visitor -> discovery welcome
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // Made purchase -> thank you + feedback ask
      e("7", "8", "output_2"), // No purchase -> return incentive
    ],
    summary:
      "Drives in-store engagement through Braze geofence-triggered messaging. When a user enters a store geofence, AI extracts location details, Braze fetches the user's in-store visit history and purchase data, a returning vs. first-time visitor condition personalizes the greeting, a real-time push notification and in-app message with 15% store offer are delivered instantly, and after 24 hours a post-visit follow-up email with feedback request or return incentive content card nurtures ongoing store loyalty.",
  },

  // ===================================================================
  // 246. NEW RELEASE ANNOUNCEMENT — Pattern I
  // ===================================================================

  "New Release Announcement": {
    nodes: [
      trigger(
        "Webhook: Braze new product/feature release event",
        "webhook_app",
        {
          source: "Braze",
          event: "product.release.published",
          howItStarts:
            "Braze fires a webhook when a new product or feature release is published, triggering a multi-channel announcement and adoption nurture sequence",
          setup:
            "Configure your product/release management system to POST new release events to this workflow URL via Braze. Include release details, target segments, and feature descriptions.",
          inputFields: [],
        },
      ),

      // 2. AI extract release details
      aiNode(
        "2",
        "AI: Extract new release details and target audience",
        280,
        "You are a product launch communications specialist. Extract release_name, release_type (product, feature, update), version, headline, key_benefits (array), target_segment, release_url, demo_url, and availability_date from the webhook payload regardless of format. Return JSON.",
        "Extract new release announcement details from: {webhook_payload}",
        [
          { field: "release_name", as: "release_name", type: "string" },
          { field: "release_type", as: "release_type", type: "string" },
          { field: "headline", as: "release_headline", type: "string" },
          { field: "key_benefits", as: "key_benefits", type: "string" },
          { field: "target_segment", as: "target_segment", type: "string" },
          { field: "release_url", as: "release_url", type: "string" },
          { field: "demo_url", as: "demo_url", type: "string" },
        ],
      ),

      // 3. Braze: Lookup user interest segment + engagement level
      brazePostNode(
        "3",
        "Braze: Fetch target segment users with interest and engagement data",
        520,
        "users/export/segment",
        [
          { key: "segment_id", value: "{target_segment}" },
          {
            key: "fields_to_export",
            value:
              '["external_id","email","first_name","custom_attributes","push_subscribe"]',
          },
        ],
        [
          {
            name: "users[0].external_id",
            customName: "external_id",
            type: "string",
          },
          { name: "users[0].email", customName: "user_email", type: "string" },
          {
            name: "users[0].first_name",
            customName: "first_name",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.engagement_level",
            customName: "engagement_level",
            type: "string",
          },
          {
            name: "users[0].push_subscribe",
            customName: "push_opted_in",
            type: "string",
          },
        ],
      ),

      // 4. Condition: Highly engaged user (engagement_level = high)?
      n(
        "4",
        "condition",
        "Is user highly engaged (engagement_level = high)?",
        760,
        {
          conditionData: [
            {
              variable: "engagement_level",
              value: "high",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. Braze: Send release announcement email + push + content card
      brazePostNode(
        "5",
        "Braze: Send release announcement via email + push + content card",
        1000,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"email":{"app_id":"{braze_app_id}","subject":"Introducing {release_name} — {release_headline}","body":"<h1>{release_headline}</h1><p>We\'re excited to announce {release_name}! Key benefits: {key_benefits}.</p><a href={release_url}>Explore Now</a><a href={demo_url}>Watch Demo</a>"},"apple_push":{"alert":"New: {release_name} is here! {release_headline}","extra":{"deep_link":"{release_url}"}},"content_card":{"title":"{release_name} is Live!","description":"{release_headline}. Explore the new {release_type} now.","extras":{"deep_link":"{release_url}"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "announce_dispatch_id",
            type: "string",
          },
        ],
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait 5 days for initial adoption period", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Condition: Has user adopted the new release (visited release URL)?
      n(
        "7",
        "condition",
        "Has user engaged with the release (visited release page)?",
        1480,
        {
          conditionData: [
            {
              variable: "engagement_level",
              value: "high",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Braze: Send deep-dive email with advanced use cases or adoption nudge
      brazePostNode(
        "8",
        "Braze: Send feature deep-dive email or adoption nudge push",
        1720,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"email":{"app_id":"{braze_app_id}","subject":"{first_name}, unlock the full potential of {release_name}","body":"<h1>Go Deeper with {release_name}</h1><p>Here are advanced use cases and pro tips to maximize your experience with {release_name}. See how power users are already getting results.</p><a href={demo_url}>Watch Advanced Tutorial</a><a href=https://community.example.com/{release_name}>Join the Discussion</a>"},"apple_push":{"alert":"Pro tips for {release_name} — unlock advanced features now.","extra":{"deep_link":"{demo_url}"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "deepdive_dispatch_id",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Highly engaged -> full cross-channel announcement
      e("4", "5", "output_2"), // Lower engagement -> email + content card only
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // Adopted -> advanced deep-dive
      e("7", "8", "output_2"), // Not adopted -> adoption nudge push
    ],
    summary:
      "Announces new product or feature releases through Braze cross-channel campaigns. When a release is published, AI extracts release details and key benefits, Braze fetches the target segment with engagement levels, an engagement condition tailors announcement intensity, a multi-channel blast via email, push, and content card delivers the announcement, and after a 5-day adoption window a deep-dive email with advanced use cases or an adoption nudge push drives feature engagement.",
  },

  // ===================================================================
  // 247. NEXT BEST ACTION RE-ENTRY CANVAS — Pattern L
  // ===================================================================

  "Next Best Action Re-Entry Canvas": {
    nodes: [
      trigger(
        "Schedule: Daily NBA calculation and action trigger",
        "schedule",
        {
          schedule: "0 8 * * * (daily at 8 AM)",
          howItStarts:
            "A daily scheduled job triggers Braze to calculate Next Best Actions for each user based on usage signals, engagement patterns, and predictive models, then routes them to the appropriate action canvas",
          setup:
            "Configure Braze Connected Content or server-side NBA model endpoint. Set up user segments for expansion, retention, education, and re-engagement tracks.",
          inputFields: [],
        },
      ),

      // 2. Braze: Lookup account context + health score
      brazePostNode(
        "2",
        "Braze: Fetch user segment with usage signals and health scores",
        280,
        "users/export/segment",
        [
          { key: "segment_id", value: "{nba_eligible_segment_id}" },
          {
            key: "fields_to_export",
            value:
              '["external_id","email","first_name","custom_attributes","push_subscribe","total_revenue"]',
          },
        ],
        [
          {
            name: "users[0].external_id",
            customName: "external_id",
            type: "string",
          },
          { name: "users[0].email", customName: "user_email", type: "string" },
          {
            name: "users[0].first_name",
            customName: "first_name",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.health_score",
            customName: "health_score",
            type: "number",
          },
          {
            name: "users[0].custom_attributes.usage_trend",
            customName: "usage_trend",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.plan_type",
            customName: "plan_type",
            type: "string",
          },
          {
            name: "users[0].total_revenue",
            customName: "lifetime_revenue",
            type: "number",
          },
        ],
      ),

      // 3. AI analyze signal and recommend NBA
      aiNode(
        "3",
        "AI: Calculate Next Best Action from usage signals and health score",
        520,
        "You are a customer success AI. Given the user health_score, usage_trend, plan_type, and lifetime_revenue, determine the next best action. Possible actions: expansion_upsell (health>70, usage_trend=growing), education_nurture (health 50-70, usage_trend=stable), save_intervention (health<50, usage_trend=declining), re_engagement (health<30). Return JSON with recommended_action, action_reason, confidence_score (0-100), recommended_channel, and personalized_message.",
        "User: {first_name}. Health: {health_score}. Usage trend: {usage_trend}. Plan: {plan_type}. LTV: ${lifetime_revenue}. Determine the next best action.",
        [
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "action_reason", as: "action_reason", type: "string" },
          { field: "confidence_score", as: "nba_confidence", type: "number" },
          {
            field: "recommended_channel",
            as: "recommended_channel",
            type: "string",
          },
          { field: "personalized_message", as: "nba_message", type: "string" },
        ],
      ),

      // 4. Condition: Expansion opportunity vs save-risk?
      n(
        "4",
        "condition",
        "Is recommended action expansion/upsell (vs save/re-engage)?",
        760,
        {
          conditionData: [
            {
              variable: "recommended_action",
              value: "expansion_upsell",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. Braze: Send contextual NBA action via recommended channel
      brazePostNode(
        "5",
        "Braze: Send NBA-driven message via optimal channel",
        1000,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"email":{"app_id":"{braze_app_id}","subject":"{first_name}, {nba_message}","body":"<h1>Your Next Step</h1><p>{nba_message}</p><p>Reason: {action_reason}. We think this is the best move for you based on your current usage patterns.</p><a href=https://app.example.com/upgrade>Take Action</a>"},"apple_push":{"alert":"{nba_message}","extra":{"deep_link":"app://action/{recommended_action}"}},"in_app_message":{"type":"slideup","header":"Recommended for You","body":"{nba_message}","buttons":[{"text":"Learn More","uri":"app://action/{recommended_action}"}]}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "nba_dispatch_id",
            type: "string",
          },
        ],
      ),

      // 6. Delay 7 days
      n(
        "6",
        "delayAction",
        "Wait 7 days for action response and usage change",
        1240,
        {
          delayDetails: { duration: 7, unit: "days" },
        },
      ),

      // 7. Condition: Usage stabilized or converted?
      n(
        "7",
        "condition",
        "Has health score improved above 60 after NBA action?",
        1480,
        {
          conditionData: [
            {
              variable: "health_score",
              value: "60",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Braze: Send second action or Slack escalation for unresolved accounts
      brazePostNode(
        "8",
        "Braze: Send follow-up action or escalation content card",
        1720,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"email":{"app_id":"{braze_app_id}","subject":"{first_name}, checking in — how can we help?","body":"<h1>We\'re Here to Help</h1><p>We noticed your usage patterns have changed. Our team is ready to help you get the most out of your {plan_type} plan. Book a complimentary strategy session.</p><a href=https://app.example.com/book-session>Book Strategy Session</a>"},"content_card":{"title":"Need help with your {plan_type} plan?","description":"Book a free strategy session with our success team.","extras":{"deep_link":"app://book-session"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "followup_dispatch_id",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Expansion opportunity -> upsell action
      e("4", "5", "output_2"), // Save/re-engage -> retention action
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"), // Not improved -> escalation follow-up
      e("7", "8", "output_1"), // Improved -> positive reinforcement
    ],
    summary:
      "Orchestrates a Next Best Action re-entry canvas via Braze with AI-powered recommendations. A daily schedule triggers Braze to fetch user segments with health scores and usage signals, AI calculates the optimal action (expansion, education, save, or re-engagement) with confidence scoring, an expansion vs. save condition routes messaging, the NBA-driven message is delivered across email, push, and in-app via the optimal channel, and after a 7-day observation window a follow-up action or escalation content card drives resolution.",
  },

  // ===================================================================
  // 248. MEDIA SUBSCRIBER RETENTION JOURNEY — Pattern G
  // ===================================================================

  "Media Subscriber Retention Journey": {
    nodes: [
      trigger("Schedule: Weekly media engagement drop detection", "schedule", {
        schedule: "0 9 * * 1 (every Monday at 9 AM)",
        howItStarts:
          "A weekly scheduled job scans Braze for media subscribers whose content engagement has dropped below threshold, triggering a retention and winback sequence",
        setup:
          "Configure Braze segments for media subscribers with declining engagement metrics. Set engagement thresholds: articles_read, streaming_minutes, or content_interactions over the past 14 days.",
        inputFields: [],
      }),

      // 2. Braze: Lookup disengaged media subscribers
      brazePostNode(
        "2",
        "Braze: Fetch disengaged media subscribers with content history",
        280,
        "users/export/segment",
        [
          { key: "segment_id", value: "{disengaged_media_segment_id}" },
          {
            key: "fields_to_export",
            value:
              '["external_id","email","first_name","custom_attributes","push_subscribe","email_subscribe"]',
          },
        ],
        [
          {
            name: "users[0].external_id",
            customName: "external_id",
            type: "string",
          },
          {
            name: "users[0].email",
            customName: "subscriber_email",
            type: "string",
          },
          {
            name: "users[0].first_name",
            customName: "first_name",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.subscription_plan",
            customName: "subscription_plan",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.favorite_categories",
            customName: "favorite_categories",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.days_since_last_engagement",
            customName: "days_inactive",
            type: "number",
          },
          {
            name: "users[0].custom_attributes.lifetime_content_consumed",
            customName: "lifetime_content",
            type: "number",
          },
        ],
      ),

      // 3. AI analyze likely inactivity reason
      aiNode(
        "3",
        "AI: Analyze media subscriber inactivity reason and content recommendation",
        520,
        "You are a media subscriber retention analyst. Given the subscriber's favorite_categories, subscription_plan, days_inactive, and lifetime_content consumed, determine the likely inactivity reason (content_fatigue, relevance_mismatch, time_constraints, competitor_switch, technical_issue) and recommend 3 personalized content pieces to re-engage them. Return JSON with inactivity_reason, recommended_content (array of title+url objects), win_back_angle, and urgency_level (high/medium/low).",
        "Subscriber: {first_name}. Plan: {subscription_plan}. Favorites: {favorite_categories}. Inactive days: {days_inactive}. Lifetime content: {lifetime_content} items. Analyze inactivity and recommend re-engagement content.",
        [
          {
            field: "inactivity_reason",
            as: "inactivity_reason",
            type: "string",
          },
          {
            field: "recommended_content",
            as: "recommended_content",
            type: "string",
          },
          { field: "win_back_angle", as: "win_back_angle", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),

      // 4. Condition: High-value subscriber (lifetime_content > 100)?
      n(
        "4",
        "condition",
        "Is this a high-value media subscriber (100+ content consumed)?",
        760,
        {
          conditionData: [
            {
              variable: "lifetime_content",
              value: "100",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. Braze: Send first winback email with curated content recommendations
      brazePostNode(
        "5",
        "Braze: Send winback email with curated content picks + push",
        1000,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"email":{"app_id":"{braze_app_id}","subject":"{first_name}, we curated new {favorite_categories} content just for you","body":"<h1>We Miss You!</h1><p>It\'s been {days_inactive} days since your last visit. Based on your love of {favorite_categories}, here\'s what you\'ve been missing:</p><p>{recommended_content}</p><p>{win_back_angle}</p><a href=https://media.example.com/for-you/{external_id}>See Your Personalized Feed</a>"},"apple_push":{"alert":"New {favorite_categories} content picked just for you. Come back and explore!","extra":{"deep_link":"app://feed/personalized"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "winback1_dispatch_id",
            type: "string",
          },
        ],
      ),

      // 6. Delay 10 days
      n("6", "delayAction", "Wait 10 days for re-engagement response", 1240, {
        delayDetails: { duration: 10, unit: "days" },
      }),

      // 7. Condition: Still inactive after first winback?
      n(
        "7",
        "condition",
        "Is subscriber still inactive (no content consumed in 10 days)?",
        1480,
        {
          conditionData: [
            {
              variable: "days_inactive",
              value: "14",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 8. Braze: Send second angle — exclusive content offer or subscription pause option
      brazePostNode(
        "8",
        "Braze: Send exclusive content offer SMS + in-app pause option",
        1720,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"sms":{"app_id":"{braze_app_id}","body":"{first_name}, we have an exclusive {favorite_categories} series waiting for you. Come back this week and unlock bonus content: https://media.example.com/exclusive/{external_id}"},"in_app_message":{"type":"modal","header":"We Miss You, {first_name}","body":"Need a break? Pause your subscription instead of canceling. Or explore our exclusive new {favorite_categories} series.","buttons":[{"text":"Explore Content","uri":"app://feed/exclusive"},{"text":"Pause Subscription","uri":"app://account/pause"}]}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "winback2_dispatch_id",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // High-value -> premium winback with exclusive content
      e("4", "5", "output_2"), // Standard -> regular winback email
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // Still inactive -> second angle with SMS + in-app
      e("7", "8", "output_2"), // Re-engaged -> skip (no further action needed)
    ],
    summary:
      "Retains media subscribers showing engagement decline via Braze multi-channel winback. A weekly schedule scans for disengaged subscribers, Braze fetches their content history and favorite categories, AI analyzes the inactivity reason and recommends personalized content, a high-value subscriber condition gates premium vs. standard winback, a curated content email and push notification are sent, and after 10 days a second rescue via SMS with exclusive content offer and in-app subscription pause option prevents churn.",
  },

  // ===================================================================
  // 249. CUSTOMER ANNIVERSARY JOURNEY — Pattern H
  // ===================================================================

  "Customer Anniversary Journey": {
    nodes: [
      trigger("Schedule: Daily customer anniversary check", "schedule", {
        schedule: "0 7 * * * (daily at 7 AM)",
        howItStarts:
          "A daily scheduled job checks Braze for customers whose sign-up anniversary is today, triggering a celebration journey with personalized rewards based on tenure and loyalty tier",
        setup:
          "Configure Braze to track customer_since_date as a custom attribute. Create a segment for users whose anniversary date matches today's date.",
        inputFields: [],
      }),

      // 2. Braze: Lookup anniversary customers with loyalty data + LTV
      brazePostNode(
        "2",
        "Braze: Fetch today's anniversary customers with loyalty and LTV data",
        280,
        "users/export/segment",
        [
          { key: "segment_id", value: "{anniversary_today_segment_id}" },
          {
            key: "fields_to_export",
            value:
              '["external_id","email","first_name","custom_attributes","total_revenue","push_subscribe"]',
          },
        ],
        [
          {
            name: "users[0].external_id",
            customName: "external_id",
            type: "string",
          },
          {
            name: "users[0].email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "users[0].first_name",
            customName: "first_name",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.customer_since_date",
            customName: "customer_since",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.loyalty_tier",
            customName: "loyalty_tier",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.total_orders",
            customName: "total_orders",
            type: "number",
          },
          {
            name: "users[0].total_revenue",
            customName: "lifetime_revenue",
            type: "number",
          },
          {
            name: "users[0].push_subscribe",
            customName: "push_subscribed",
            type: "string",
          },
        ],
      ),

      // 3. Condition: VIP tier (Gold or Platinum)?
      n("3", "condition", "Is customer VIP tier (Gold or Platinum)?", 520, {
        conditionData: [
          {
            variable: "loyalty_tier",
            value: "Gold",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "loyalty_tier",
            value: "Platinum",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 4. Braze: Send anniversary celebration email + push with reward
      brazePostNode(
        "4",
        "Braze: Send anniversary celebration email + push with personalized reward",
        760,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"email":{"app_id":"{braze_app_id}","subject":"Happy Anniversary, {first_name}! A special gift inside","body":"<h1>Happy Anniversary!</h1><p>It\'s been another amazing year together, {first_name}! Since {customer_since}, you\'ve placed {total_orders} orders and been an incredible {loyalty_tier} member.</p><p>Here\'s a special anniversary reward: 20% off your next order with code ANNIVERSARY20.</p><a href=https://shop.example.com/anniversary/{external_id}>Claim Your Reward</a>"},"apple_push":{"alert":"Happy Anniversary, {first_name}! Your special 20% off reward is waiting inside.","extra":{"deep_link":"app://anniversary/reward"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "celebration_dispatch_id",
            type: "string",
          },
        ],
      ),

      // 5. Delay 6 days
      n(
        "5",
        "delayAction",
        "Wait 6 days for anniversary reward redemption",
        1000,
        {
          delayDetails: { duration: 6, unit: "days" },
        },
      ),

      // 6. Condition: Reward redeemed?
      n(
        "6",
        "condition",
        "Has customer redeemed the anniversary reward?",
        1240,
        {
          conditionData: [
            {
              variable: "total_orders",
              value: "0",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 7. Braze: Send redemption reminder or next-level loyalty teaser via content card + in-app
      brazePostNode(
        "7",
        "Braze: Send reward reminder in-app message or next-tier content card",
        1480,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"in_app_message":{"type":"modal","header":"Anniversary Reward Expiring Soon!","body":"{first_name}, your 20% off anniversary code ANNIVERSARY20 expires in 24 hours. Don\'t miss out!","buttons":[{"text":"Shop Now","uri":"https://shop.example.com/anniversary/{external_id}"}]},"content_card":{"title":"Last chance: Anniversary 20% off","description":"Your anniversary reward expires tomorrow. Use code ANNIVERSARY20 before it\'s gone!","extras":{"offer_code":"ANNIVERSARY20","deep_link":"app://shop"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "reminder_dispatch_id",
            type: "string",
          },
        ],
      ),

      // 8. Google Sheets: Log anniversary milestone + redemption status
      sheetsNode(
        "8",
        "Google Sheets: Log anniversary milestone and redemption outcome",
        1720,
        "Anniversary_Milestones",
        '[["{external_id}","{customer_email}","{first_name}","{customer_since}","{loyalty_tier}","{total_orders}","{lifetime_revenue}","{celebration_dispatch_id}","{new Date().toISOString()}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // VIP -> premium anniversary reward
      e("3", "4", "output_2"), // Standard -> standard anniversary reward
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"), // Not redeemed -> reminder in-app + content card
      e("6", "7", "output_1"), // Redeemed -> next-tier teaser
      e("7", "8"),
    ],
    summary:
      "Celebrates customer anniversaries via Braze cross-channel messaging with personalized rewards. A daily schedule checks for customers whose sign-up anniversary is today, Braze fetches loyalty tier, order history, and LTV, a VIP condition gates premium vs. standard reward tiers, a celebration email and push notification with 20% off anniversary code are delivered, after 6 days an unredeemed reward triggers an in-app reminder and content card, and the milestone outcome is logged to Google Sheets for tracking.",
  },

  // ===================================================================
  // 250. SMS + PUSH + EMAIL CROSS-CHANNEL RESCUE — Pattern B (triple-channel cascade)
  // ===================================================================

  "SMS + Push + Email Cross-Channel Rescue": {
    nodes: [
      trigger(
        "Webhook: Braze cart abandonment event with cross-channel rescue",
        "webhook_app",
        {
          source: "Braze",
          event: "cart.abandoned",
          howItStarts:
            "Braze fires a webhook when a user abandons their cart, triggering a triple-channel cascade rescue sequence that sends SMS first, then push, then email to maximize recovery",
          setup:
            "Configure Braze to POST cart abandonment events to this workflow URL. Ensure SMS, push, and email channels are enabled with proper consent tracking.",
          inputFields: [],
        },
      ),

      // 2. AI extract cart details
      aiNode(
        "2",
        "AI: Extract abandoned cart details for cross-channel rescue",
        280,
        "You are a cart recovery specialist. Extract external_id, email, phone_number, first_name, cart_items (array with name, price, image_url), cart_total, currency, cart_url, and top_product_name from the webhook payload regardless of format. Return JSON.",
        "Extract abandoned cart details for rescue from: {webhook_payload}",
        [
          { field: "external_id", as: "external_id", type: "string" },
          { field: "email", as: "shopper_email", type: "string" },
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "cart_total", as: "cart_total", type: "number" },
          { field: "top_product_name", as: "top_product", type: "string" },
          { field: "cart_url", as: "cart_url", type: "string" },
        ],
      ),

      // 3. Braze: Lookup customer channel preferences + purchase history
      brazePostNode(
        "3",
        "Braze: Fetch shopper profile with channel opt-ins and purchase history",
        520,
        "users/export/ids",
        [
          { key: "external_ids", value: '["{ external_id }"]' },
          {
            key: "fields_to_export",
            value:
              '["custom_attributes","email_subscribe","push_subscribe","phone","total_revenue"]',
          },
        ],
        [
          {
            name: "users[0].email_subscribe",
            customName: "email_opted_in",
            type: "string",
          },
          {
            name: "users[0].push_subscribe",
            customName: "push_opted_in",
            type: "string",
          },
          {
            name: "users[0].custom_attributes.sms_opted_in",
            customName: "sms_opted_in",
            type: "string",
          },
          {
            name: "users[0].total_revenue",
            customName: "lifetime_revenue",
            type: "number",
          },
        ],
      ),

      // 4. Condition: Cart value > $100 (high-value rescue)?
      n(
        "4",
        "condition",
        "Is cart value over $100 for high-priority rescue?",
        760,
        {
          conditionData: [
            {
              variable: "cart_total",
              value: "100",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 5. Braze: Send immediate SMS rescue (channel 1 — fastest)
      brazePostNode(
        "5",
        "Braze: Send immediate SMS cart rescue (Channel 1 — fastest reach)",
        1000,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"sms":{"app_id":"{braze_app_id}","body":"{first_name}, you left {top_product} in your cart (${cart_total}). Complete your order now before it sells out: {cart_url}"}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "sms_rescue_dispatch_id",
            type: "string",
          },
        ],
      ),

      // 6. Delay 4 hours for push follow-up
      n(
        "6",
        "delayAction",
        "Wait 4 hours before push notification follow-up",
        1240,
        {
          delayDetails: { duration: 4, unit: "hours" },
        },
      ),

      // 7. Braze: Send push notification rescue (channel 2)
      brazePostNode(
        "7",
        "Braze: Send push notification cart rescue (Channel 2 — visual)",
        1480,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"apple_push":{"alert":"{first_name}, {top_product} is still in your cart! 12 other shoppers are eyeing it. Tap to checkout now.","extra":{"deep_link":"{cart_url}","cart_total":"{cart_total}"}},"content_card":{"title":"Your cart is waiting!","description":"{top_product} and ${cart_total} total — checkout before items sell out.","extras":{"deep_link":"{cart_url}"}}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "push_rescue_dispatch_id",
            type: "string",
          },
        ],
      ),

      // 8. Braze: Send email rescue with full cart details (channel 3 — detailed)
      brazePostNode(
        "8",
        "Braze: Send detailed email cart rescue with product images (Channel 3)",
        1720,
        "messages/send",
        [
          { key: "external_user_ids", value: '["{ external_id }"]' },
          {
            key: "messages",
            value:
              '{"email":{"app_id":"{braze_app_id}","subject":"{first_name}, complete your ${cart_total} order — items selling fast!","body":"<h1>Your Cart is Waiting</h1><p>{first_name}, you left some great items behind. Your {top_product} and other selections totaling ${cart_total} are still available.</p><p>12 other shoppers have this item in their cart right now. Don\'t miss out!</p><a href={cart_url}>Complete Your Order</a><p>Need help? Reply to this email or chat with us anytime.</p>"}}',
          },
        ],
        [
          {
            name: "dispatch_id",
            customName: "email_rescue_dispatch_id",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // High-value cart -> aggressive triple-channel rescue
      e("4", "5", "output_2"), // Lower-value -> SMS + email only
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Executes a triple-channel cascade cart rescue via Braze SMS, push, and email. When a cart is abandoned, AI extracts cart details including product names and total, Braze fetches channel opt-ins and purchase history, a high-value cart condition gates rescue intensity, an immediate SMS is sent first for fastest reach, after 4 hours a push notification with social proof and content card provide visual urgency, and finally a detailed email with full cart layout and product images completes the cross-channel rescue cascade.",
  },
};
